import { useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import _isEmpty from 'lodash/isEmpty'
import { FileRejection } from 'react-dropzone'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { H4 } from 'components/Typography'
import { JobPageContext } from 'features/jobs/JobPageContext'
import DropzoneModal from 'components/Modal/DropzoneModal'
import { JobInfoImagesMessages } from './messages'
import {
  fetchJobImagesThunk,
  jobImagesActions,
  uploadJobImageFullAndThumbThunk,
} from 'features/jobs/slice'
import { SUPPORTED_IMAGE_MIME_TYPES } from 'features/jobs/consts'
import {
  jobInfoImagesDataSelector,
  jobInfoImagesThumbsSelector,
} from 'features/jobs/selectors'
import { SpinnerCentered } from 'components/Loader'
import { commonMessages } from 'app/i18n/messages'
import { JobImageData } from 'features/jobs/types'
import JobImageBlock from './JobImageBlock'
import {
  JobImagesBlock,
  JobImagesContainer,
  JobImagesErrorWrapper,
  JobImagesHeader,
  JobImagesSpinnerWrapper,
  JobImagesWrapper,
  JobImageUploadButton,
} from './styles'

function JobInfoImages() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { jobId } = useContext(JobPageContext)

  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isUploadingImageError, setIsUploadingImageError] = useState(false)

  const isFetching = useAppSelector(jobInfoImagesDataSelector.isFetching)
  const isFinished = useAppSelector(jobInfoImagesDataSelector.isFinished)
  const isError = useAppSelector(jobInfoImagesDataSelector.error)
  const jobImagesThumbs = useAppSelector(jobInfoImagesThumbsSelector)

  useEffect(() => {
    dispatch(fetchJobImagesThunk(jobId))

    return () => {
      dispatch(jobImagesActions.clear())
    }
  }, [dispatch, jobId])

  const handleDropFiles = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const imageFull = acceptedFiles[0]

    // do nothing if has file rejections or no image, Dropzone component will handle error
    if (!_isEmpty(fileRejections) || !imageFull) {
      return
    }

    try {
      setIsUploadingImage(true)

      await dispatch(uploadJobImageFullAndThumbThunk({ imageFull, jobId }))

      setIsOpenUploadModal(false)
    } catch (e) {
      console.log(`Error uploading image ${imageFull.name}`, e)
      setIsUploadingImageError(true)
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleUploadModal = () => {
    setIsUploadingImageError(false)
    setIsOpenUploadModal(true)
  }

  return (
    <>
      <JobImagesContainer>
        <JobImagesHeader>
          <H4>
            <FormattedMessage {...JobInfoImagesMessages.title} />
          </H4>
          <JobImageUploadButton
            variant="secondary"
            type="button"
            onClick={handleUploadModal}
          >
            <FormattedMessage {...JobInfoImagesMessages.uploadButtonLabel} />
          </JobImageUploadButton>
        </JobImagesHeader>

        {isFetching && !isFinished && (
          <JobImagesSpinnerWrapper>
            <SpinnerCentered />
          </JobImagesSpinnerWrapper>
        )}
        {isError && (
          <JobImagesBlock>
            <FormattedMessage {...commonMessages.commonError} />
          </JobImagesBlock>
        )}
        {isFinished && (
          <>
            {_isEmpty(jobImagesThumbs) ? (
              <JobImagesBlock>
                <FormattedMessage
                  {...JobInfoImagesMessages.noJobImagesMessage}
                />
              </JobImagesBlock>
            ) : (
              <JobImagesWrapper>
                {jobImagesThumbs.map((jobImageThumb: JobImageData) => (
                  <JobImageBlock
                    key={jobImageThumb.filename}
                    jobImageThumb={jobImageThumb}
                  />
                ))}
              </JobImagesWrapper>
            )}
          </>
        )}
      </JobImagesContainer>

      {isOpenUploadModal && (
        <DropzoneModal
          isOpen
          title={formatMessage(JobInfoImagesMessages.uploadModalTitle)}
          handleFiles={handleDropFiles}
          onClose={() => setIsOpenUploadModal(false)}
          accept={SUPPORTED_IMAGE_MIME_TYPES}
          description={formatMessage(
            JobInfoImagesMessages.dropZoneModalDescription
          )}
        >
          {isUploadingImage && <SpinnerCentered />}
          {isUploadingImageError && (
            <JobImagesErrorWrapper>
              <FormattedMessage {...commonMessages.commonError} />
            </JobImagesErrorWrapper>
          )}
        </DropzoneModal>
      )}
    </>
  )
}

export default JobInfoImages
